import MainBody from "../../components/MainBody/MainBody";
import React, { useState } from "react";
import { Check, Gem, Rocket, Shield } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutForm from "./CheckoutForm";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const stripePromise = loadStripe("your-publishable-key");

const plans = {
  monthly: [
    {
      id: "professional-monthly",
      name: "Professional",
      price: "$10/mo",
      amount: 1000,
      features: ["1 Trust", "Basic Support", "100MB Storage"],
      stripePriceId: "price_123_professional_monthly",
    },
    {
      id: "hybrid-monthly",
      name: "Hybrid",
      price: "$25/mo",
      amount: 2500,
      features: ["5 Trusts", "Priority Support", "5GB Storage", "Advanced Analytics"],
      stripePriceId: "price_123_hybrid_monthly",
    },
    {
      id: "ultimate-monthly",
      name: "Ultimate",
      price: "$50/mo",
      amount: 5000,
      features: ["Unlimited Trusts", "24/7 Support", "100GB Storage", "Team Collaboration", "Custom Reports"],
      stripePriceId: "price_123_ultimate_monthly",
    },
  ],
  yearly: [
    {
      id: "professional-yearly",
      name: "Professional",
      price: "$100/yr",
      amount: 10000,
      features: ["1 Trust", "Basic Support", "100MB Storage"],
      stripePriceId: "price_123_professional_yearly",
    },
    {
      id: "hybrid-yearly",
      name: "Hybrid",
      price: "$250/yr",
      amount: 25000,
      features: ["5 Trusts", "Priority Support", "5GB Storage", "Advanced Analytics"],
      stripePriceId: "price_123_hybrid_yearly",
    },
    {
      id: "ultimate-yearly",
      name: "Ultimate",
      price: "$500/yr",
      amount: 50000,
      features: ["Unlimited Trusts", "24/7 Support", "100GB Storage", "Team Collaboration", "Custom Reports"],
      stripePriceId: "price_123_ultimate_yearly",
    },
  ],
};


const Subscription = ({expandList=false}) => {
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [open, setOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  // const [clientSecret, setClientSecret] = useState(null);

  const handleSubscribe = async (plan) => {
    // const res = await fetch("/api/create-payment-intent", {
    //   method: "POST",
    //   headers: { "Content-Type": "application/json" },
    //   body: JSON.stringify({ priceId: plan.stripePriceId }),
    // });
    // const data = await res.json();
    // setClientSecret(data.clientSecret);
    setSelectedPlan(plan);
    setOpen(true);
  };

  return (
    <>
      <MainBody
        search={false}
        actionContent={""}
        // loading={loading}
        className={expandList ? "bg-secondary p-0" : "bg-secondary"}
        expandList={expandList}
      >
        <div className="max-w-6xl mx-auto p-6">
          <Tabs value={billingCycle} onValueChange={setBillingCycle}>
            <div className="flex justify-center mb-6">
              <TabsList className="relative inline-flex rounded-lg p-0 bg-muted">
                <motion.div
                  layout
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  className={`absolute top-1 left-1 w-1/2 h-[calc(100%-0.5rem)] rounded-md shadow-md z-0 translate-x-[${
                    billingCycle === "yearly" ? "100%" : "0%"
                  }]`}
                />
                <TabsTrigger value="monthly" className=" rounded-se-none text-md hover:bg-webSecondary rounded-ee-none z-10 w-24 text-center bg-background">
                  Monthly
                </TabsTrigger>
                <TabsTrigger value="yearly" className="rounded-ss-none text-md hover:bg-webSecondary rounded-es-none z-10 w-24 text-center bg-background">
                  Yearly
                </TabsTrigger>
              </TabsList>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={billingCycle}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6"
              >
                {plans[billingCycle].map((plan) => {
                  const planType = plan.name.toLowerCase();

                  let icon = Gem;
                  let highlight = "";
                  let styles = {
                    border: "border-gray-300",
                    text: "text-gray-800",
                    bg: "bg-gray-800",
                    hover: "hover:bg-gray-900",
                    badge: "bg-gray-500",
                  };
                  
                  if (planType.includes("professional")) {
                    icon = Shield;
                    styles = {
                      border: "border-green-500",
                      text: "text-green-600",
                      bg: "bg-green-600",
                      hover: "hover:bg-green-700",
                      badge: "bg-green-500",
                    };
                  } else if (planType.includes("hybrid")) {
                    icon = Rocket;
                    highlight = "Most Popular";
                    styles = {
                      border: "border-blue-500",
                      text: "text-blue-600",
                      bg: "bg-blue-600",
                      hover: "hover:bg-blue-700",
                      badge: "bg-blue-500",
                    };
                  } else if (planType.includes("ultimate")) {
                    icon = Gem;
                    highlight = "Best Value";
                    styles = {
                      border: "border-purple-500",
                      text: "text-purple-600",
                      bg: "bg-purple-600",
                      hover: "hover:bg-purple-700",
                      badge: "bg-purple-500",
                    };
                  }

                  const Icon = icon;

                  return (
                    <Card
                      key={plan.id}
                      className={`relative flex flex-col justify-between p-6 h-full ${styles.border} shadow-md hover:shadow-lg transition duration-300 bg-background`}
                    >
                      {highlight && (
                        <div
                          className={`absolute -top-3 right-4 ${styles.badge} text-white text-xs px-2 py-1 rounded-full shadow-sm`}
                        >
                          {highlight}
                        </div>
                      )}

                      <div>
                        <div
                          className={`flex items-center gap-2 mb-2 ${styles.text}`}
                        >
                          <Icon className="w-6 h-6" />
                          <h3 className="text-xl font-semibold">{plan.name}</h3>
                        </div>
                        <p className="text-3xl font-bold mb-4">
                          {plan.price}
                        </p>

                        <ul className="space-y-2 text-sm mb-6">
                          {plan.features.map((feature, idx) => (
                            <li key={idx} className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-green-500" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <Button
                        onClick={() => handleSubscribe(plan)}
                        className={`w-full ${styles.bg} ${styles.hover} text-white`}
                      >
                        Subscribe
                      </Button>
                    </Card>
                  );
                })}
              </motion.div>
            </AnimatePresence>
          </Tabs>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Complete your purchase</DialogTitle>
              </DialogHeader>
              {/* {clientSecret && ( */}
              <Elements stripe={stripePromise}>
                <CheckoutForm plan={selectedPlan} />
              </Elements>
              {/* )} */}
            </DialogContent>
          </Dialog>
        </div>
      </MainBody>
    </>
  );
};

export default Subscription;
